import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useAuthStore } from '../stores/authStore'
import { useOnboardingStore } from '../stores/onboardingStore'
import { paymentService } from '../services/paymentService-v2'
import type { UserServices } from '../services/paymentService-v2'
import { 
  CheckCircle,
  CreditCard,
  Mail,
  Phone,
  Clock,
  ArrowRight,
  Home,
  FileText,
  Calendar,
  DollarSign,
  User,
  Building,
  Loader2
} from 'lucide-react'

export const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const { data } = useOnboardingStore()
  const [userServices, setUserServices] = useState<UserServices | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Get payment intent ID from URL params if available
  const paymentIntentId = searchParams.get('payment_intent')
  const paymentStatus = searchParams.get('payment_status')

  useEffect(() => {
    fetchUserStatus()
  }, [])

  const fetchUserStatus = async () => {
    try {
      const token = localStorage.getItem('stratiqx_access_token')
      if (token) {
        paymentService.setAccessToken(token)
        const services = await paymentService.getUserServices()
        setUserServices(services)
      }
    } catch (error) {
      console.error('Failed to fetch user services:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Just now'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getServiceTitle = () => {
    const activeService = userServices?.activeServices?.[0]
    return activeService?.serviceName || 'Strategic Intelligence Service'
  }

  const getNextSteps = () => {
    return [
      {
        icon: <Mail className="w-5 h-5 text-blue-600" />,
        title: 'Payment Confirmation Email',
        description: `Detailed receipt and service agreement sent to ${user?.email}`,
        timeframe: 'Within 5 minutes'
      },
      {
        icon: <Phone className="w-5 h-5 text-green-600" />,
        title: 'Analyst Contact',
        description: 'Your dedicated intelligence analyst will reach out to discuss your requirements',
        timeframe: 'Within 24 hours'
      },
      {
        icon: <FileText className="w-5 h-5 text-purple-600" />,
        title: 'First Intelligence Report',
        description: 'Your initial strategic intelligence analysis and market insights',
        timeframe: 'Within 5 business days'
      }
    ]
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mt-1">
                Your strategic intelligence service is now active
              </p>
            </div>
            <Badge className="bg-green-100 text-green-800 border-green-300">
              <CheckCircle className="w-4 h-4 mr-1" />
              Payment Confirmed
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Success Message */}
        <Card className="mb-8 border-green-200 bg-green-50">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-green-900 mb-4">
                Thank You for Your Payment!
              </h2>
              <p className="text-lg text-green-800 mb-2">
                Your $500 deposit has been successfully processed and your strategic intelligence service is now active.
              </p>
              <p className="text-green-700">
                Welcome to StratIQX Intelligence Services, {user?.fullName || 'valued client'}!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Order Summary */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Service</span>
                <span className="font-medium">{getServiceTitle()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Deposit Amount</span>
                <span className="text-xl font-bold text-green-600">$500.00</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Payment Method</span>
                <span className="font-medium">Card ending in ****</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Transaction Date</span>
                <span className="font-medium">{formatDate(userServices?.activeServices?.[0]?.activatedAt)}</span>
              </div>
              {paymentIntentId && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Payment ID</span>
                  <span className="font-mono text-sm">{paymentIntentId.substring(0, 20)}...</span>
                </div>
              )}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge className="bg-green-100 text-green-800 border-green-300">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Confirmed
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading account details...</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account Holder</span>
                    <span className="font-medium">{user?.fullName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Email</span>
                    <span className="font-medium">{user?.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Company</span>
                    <span className="font-medium">{data.contactInfo?.companyName || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Service Plan</span>
                    <span className="font-medium capitalize">{userServices?.activeServices?.[0]?.serviceName || 'Professional Service'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account Status</span>
                    <Badge className={userServices?.hasActiveServices ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}>
                      {userServices?.hasActiveServices ? 'Active' : 'Activating'}
                    </Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              What Happens Next?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {getNextSteps().map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-gray-900">{step.title}</h4>
                      <span className="text-sm text-blue-600 font-medium">{step.timeframe}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-3 font-semibold flex items-center gap-2"
            size="lg"
          >
            <Home className="w-5 h-5" />
            Return to Home
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open('mailto:intelligence@stratiqx.ai', '_blank')}
            className="px-8 py-3 font-semibold flex items-center gap-2"
            size="lg"
          >
            <Mail className="w-5 h-5" />
            Contact Support
          </Button>
        </div>

        {/* Contact Information */}
        <Card className="mt-8 border-gray-200 bg-gray-50">
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Need Immediate Assistance?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Our intelligence team is ready to help you get started
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4" />
                  <span>intelligence@stratiqx.ai</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4" />
                  <span>24/7 Support Available</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}